import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CircleNotch, CaretLeft, User as UserIcon, Warning, CheckCircle,
  Copy, Bug, MagnifyingGlass, LockKey, CaretDown, CaretRight,
} from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import {
  SupportConversation,
  useSupportTicket,
  type SupportConversationMessage,
} from '@/components/platform/SupportConversation';
import { AssigneeControl, type Assignee } from '@/components/platform/SupportAssignee';
import { RequestTimeline } from '@/components/platform/RequestTimeline';
import type { RequestTimelineEntry } from '@/lib/supportRequestTimeline';
import {
  SupportLifecycleActions,
  SupportStatusBadge,
} from '@/components/platform/SupportLifecycleControls';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * Ticket detail — and the investigation drawer, which is the point of the whole feature.
 *
 * The layout follows the support executive's actual sequence:
 *   WHO reported it → WHAT they said → WHAT the system observed → WHERE it failed →
 *   WHAT to tell a developer → reply.
 *
 * Levels are kept visually separate on purpose (brief §8): the ✓/✕ narrative is Level 1 and is
 * always visible; the technical detail is Level 2 and sits behind a disclosure; raw developer
 * logs are Level 3 and are NOT here at all — the request id is the bridge to them.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

interface TicketDetail {
  id: string;
  title: string;
  body: string;
  status: string;
  priority: string;
  category: string;
  createdAt: string;
  resolvedAt: string | null;
  requesterUserId: string | null;
  requesterRole: string | null;
  requesterEmail: string | null;
  requesterName: string | null;
  reportedErrorCode: string | null;
  reportedCorrelationId: string | null;
  reportedTargetType: string | null;
  reportedTargetId: string | null;
  reportedFailure: { code: string; explanation: string; service: string; operation: string; owner: string } | null;
  chambers: { id: string; name: string; slug: string | null } | null;
  assignee: Assignee | null;
  messages: SupportConversationMessage[];
}

interface InvestigationStep {
  outcome: 'ok' | 'failed';
  at: string;
  label: string;
  failureCode?: string;
  repeats: number;
}

interface InvestigationFailure {
  kind: string;
  occurrences: number;
  firstAt: string;
  code: string;
  explanation: string;
  service: string;
  operation: string;
  owner: string;
  statusCode: number;
  correlationId: string | null;
  occurredAt: string;
  method: string | null;
  path: string | null;
  target: { type: string; id: string } | null;
  technical: { message: string | null; stack: string | null };
}

interface Investigation {
  available: boolean;
  reason?: string;
  window: { from: string; to: string };
  scopedToTarget: { type: string; id: string } | null;
  steps: InvestigationStep[];
  failures: InvestigationFailure[];
  /** Phase 4B. Raw HTTP evidence, oldest → newest. Kept separate from `failures` on purpose. */
  requests: RequestTimelineEntry[];
  truncated: { activity: boolean; diagnostics: boolean; requests: boolean };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(s: string) {
  return new Date(s).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}
function time(s: string) {
  return new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function roleLabel(r: string | null) {
  if (!r) return 'Unknown role';
  return r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
async function copy(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error('Could not copy to clipboard');
  }
}

const OWNER_LABEL: Record<string, string> = {
  developer: 'Needs a developer',
  infra: 'Infrastructure — may self-resolve',
  support: 'Support can resolve this',
};

// ─── Investigation drawer ─────────────────────────────────────────────────────

function TechnicalDetail({ f }: { f: InvestigationFailure }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t border-plat-border pt-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs font-medium text-plat-secondary hover:text-plat-fg"
      >
        {open ? <CaretDown className="h-3 w-3" /> : <CaretRight className="h-3 w-3" />}
        View technical details
      </button>
      {open && (
        <dl className="mt-3 grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-xs">
          <dt className="text-plat-muted">Error code</dt>
          <dd className="font-mono text-plat-fg">{f.code}</dd>
          <dt className="text-plat-muted">Service</dt>
          <dd className="text-plat-fg">{f.service}</dd>
          <dt className="text-plat-muted">Operation</dt>
          <dd className="text-plat-fg">{f.operation}</dd>
          <dt className="text-plat-muted">HTTP</dt>
          <dd className="text-plat-fg">{f.statusCode === 0 ? 'n/a (client-side)' : f.statusCode}</dd>
          {f.method && f.path && (
            <>
              <dt className="text-plat-muted">Request</dt>
              <dd className="font-mono text-plat-fg">{f.method} {f.path}</dd>
            </>
          )}
          {f.target && (
            <>
              <dt className="text-plat-muted">Object</dt>
              <dd className="font-mono text-plat-fg">{f.target.type} {f.target.id}</dd>
            </>
          )}
          <dt className="text-plat-muted">Timestamp</dt>
          <dd className="text-plat-fg">{fmt(f.occurredAt)}</dd>
          <dt className="text-plat-muted">Request ID</dt>
          <dd className="flex items-center gap-2">
            <span className="font-mono text-plat-fg">{f.correlationId ?? '—'}</span>
            {f.correlationId && (
              <button
                onClick={() => copy(f.correlationId!, 'Request ID')}
                className="inline-flex items-center gap-1 rounded border border-plat-border px-1.5 py-0.5 text-[11px] text-plat-secondary hover:bg-plat-hover"
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
            )}
          </dd>
        </dl>
      )}
    </div>
  );
}

function InvestigationPanel({
  ticketId,
  reportedCorrelationId,
}: {
  ticketId: string;
  reportedCorrelationId: string | null;
}) {
  const { data, isLoading, error } = useQuery<Investigation>({
    queryKey: ['platform', 'support', ticketId, 'investigation'],
    queryFn: async () => {
      const res = await platformApi.get(`/support/${ticketId}/investigation`);
      return res.data?.data ?? res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-plat-muted">
        <CircleNotch className="h-4 w-4 animate-spin" /> Correlating activity…
      </div>
    );
  }
  if (error) {
    return <div className="py-6 text-sm text-plat-danger-text">{getPlatformApiErrorMessage(error)}</div>;
  }
  if (!data?.available) {
    return <div className="py-6 text-sm text-plat-muted">{data?.reason ?? 'No investigation available.'}</div>;
  }

  return (
    <div className="space-y-5">
      <p className="text-xs text-plat-muted">
        Activity for this requester between {fmt(data.window.from)} and {fmt(data.window.to)}
        {data.scopedToTarget
          ? <> — filtered to <span className="font-mono">{data.scopedToTarget.type} {data.scopedToTarget.id.slice(0, 8)}…</span></>
          : ''}.
      </p>

      {/* Level 1 — the ✓/✕ narrative. */}
      {data.steps.length === 0 ? (
        <p className="text-sm text-plat-muted">No recorded activity in this window.</p>
      ) : (
        <ol className="space-y-1.5">
          {data.steps.map((s, i) => (
            <li key={`${s.at}-${i}`} className="flex items-baseline gap-3 text-sm">
              <span className="w-[72px] shrink-0 font-mono text-xs text-plat-muted">{time(s.at)}</span>
              <span className={`w-4 shrink-0 font-semibold ${s.outcome === 'ok' ? 'text-plat-success-text' : 'text-plat-danger-text'}`}>
                {s.outcome === 'ok' ? '✓' : '✕'}
              </span>
              <span className={s.outcome === 'failed' ? 'font-medium text-plat-fg' : 'text-plat-secondary'}>
                {s.label}
                {s.repeats > 1 && <span className="ml-1.5 text-plat-muted">&times;{s.repeats}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}

      {/* Level 2 — the failure summary. */}
      {data.failures.length > 0 && (
        <div className="rounded-lg border border-plat-danger-soft bg-plat-danger-soft/30 p-4">
          <div className="mb-1 flex items-center gap-2">
            <Warning className="h-4 w-4 text-plat-danger-text" weight="fill" />
            <h4 className="text-sm font-semibold text-plat-fg">
              {data.failures.length === 1 ? 'Failure detected' : `${data.failures.length} failures detected`}
            </h4>
          </div>
          {data.failures.map((f) => (
            <div key={`${f.code}-${f.target?.id ?? ''}`} className="mt-3 first:mt-2">
              <p className="text-sm text-plat-fg">{f.explanation}</p>
              <p className="mt-1 text-xs text-plat-muted">
                {OWNER_LABEL[f.owner] ?? f.owner} · {f.service}
                {/* An honest count instead of the same diagnosis repeated. A barrister retries
                    three or four times before writing in; four identical panels made the
                    executive scroll past the diagnosis rather than read it. */}
                {f.occurrences > 1 && (
                  <> · <span className="font-medium text-plat-fg">happened {f.occurrences}&times;</span>, first at {time(f.firstAt)}</>
                )}
              </p>
              <TechnicalDetail f={f} />
            </div>
          ))}
        </div>
      )}

      {/* Level 1.5 — the raw HTTP evidence. Deliberately its own section between the business
          steps above and the diagnosed failures below: a request row and a failure row can
          describe the same 404 and it is correct for both to appear. One says what was asked,
          the other says what was wrong with it. No extra fetch — this rides the investigation
          response already loaded above. */}
      <RequestTimeline
        requests={data.requests ?? []}
        truncated={data.truncated?.requests ?? false}
        reportedCorrelationId={reportedCorrelationId}
      />

      {data.failures.length === 0 && data.steps.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-plat-border bg-plat-surface p-3">
          <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-plat-success-text" weight="fill" />
          <p className="text-sm text-plat-secondary">
            No system failure was recorded for this user in this window. The problem may be
            client-side, may predate the window, or may be a usage question rather than a fault.
          </p>
        </div>
      )}

      {(data.truncated.activity || data.truncated.diagnostics) && (
        <p className="text-xs text-plat-warning-text">
          Showing a capped view — there was more activity in this window than is displayed.
        </p>
      )}

      <p className="flex items-start gap-1.5 text-xs text-plat-muted">
        <LockKey className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Metadata only. Document names, matter names, message bodies and case content are never
        shown here, and opening this view is recorded in the Activity Log.
      </p>
    </div>
  );
}

// ─── Escalation ───────────────────────────────────────────────────────────────

function EscalationModal({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'support', ticketId, 'escalation'],
    queryFn: async () => {
      const res = await platformApi.get(`/support/${ticketId}/escalation`);
      return res.data?.data ?? res.data;
    },
  });

  const text = data
    ? [
        `Ticket:      ${data.ticketId}`,
        `User:        ${data.requester?.name ?? '—'} (${data.requester?.email ?? '—'})`,
        `Role:        ${roleLabel(data.requester?.role)}`,
        `Chambers:    ${data.organisation?.name ?? 'Platform'}`,
        '',
        `Problem:     ${data.reportedProblem}`,
        '',
        data.observedFailure
          ? [
              `Failure:     ${data.observedFailure.operation} failed`,
              `Error:       ${data.observedFailure.code}`,
              `Service:     ${data.observedFailure.service}`,
              `HTTP:        ${data.observedFailure.httpStatus ?? '—'}`,
              `Request ID:  ${data.observedFailure.correlationId ?? '—'}`,
              `Endpoint:    ${data.observedFailure.method ?? ''} ${data.observedFailure.path ?? ''}`.trim(),
              `Timestamp:   ${data.observedFailure.occurredAt ? fmt(data.observedFailure.occurredAt) : '—'}`,
            ].join('\n')
          : 'Failure:     none recorded',
        data.target ? `Object:      ${data.target.type} ${data.target.id}` : '',
        '',
        'Customer description:',
        data.customerDescription,
      ].filter(Boolean).join('\n')
    : '';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 px-4 py-8">
      <ScrollLock />
      <div className="my-auto w-full max-w-2xl rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
        <h3 className="text-[15px] font-semibold text-plat-fg">Escalate to developer</h3>
        <p className="mt-1 text-sm text-plat-muted">
          Everything engineering needs to start. No document contents, matter names or case
          material are included — the Request ID is how a developer reaches the full trace.
        </p>
        {isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-plat-muted">
            <CircleNotch className="h-4 w-4 animate-spin" /> Building…
          </div>
        ) : (
          <pre className="mt-4 max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-lg border border-plat-border bg-plat-bg p-4 font-mono text-xs text-plat-fg">
            {text}
          </pre>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover">
            Close
          </button>
          <button
            onClick={() => copy(text, 'Escalation')}
            disabled={!text}
            className="inline-flex items-center gap-1.5 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
          >
            <Copy className="h-4 w-4" /> Copy for developer
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

// No STATUSES list here any more. The console no longer lets an operator pick a status out of
// the air — see lib/supportLifecycle.ts for what is offered in each state and why.
const PRIORITIES = ['low', 'normal', 'high', 'critical'];

export default function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [investigating, setInvestigating] = useState(false);

  const { data: ticket, isLoading } = useSupportTicket<TicketDetail>(id);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['platform', 'support'] });
  };

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => platformApi.patch(`/support/${id}`, body),
    onSuccess: () => { invalidate(); toast.success('Ticket updated'); },
    onError: (err) => {
      // A 400 here means the server refused the transition — almost always because this tab is
      // looking at a stale ticket that someone else already moved. Say so, then REFETCH, so the
      // buttons on screen become the ones that are actually available now. Never swallow it, and
      // never retry: the correct next action may be a different one entirely.
      toast.error(getPlatformApiErrorMessage(err));
      invalidate();
    },
  });

  if (isLoading || !ticket) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate('/platform/support')}
        className="inline-flex items-center gap-1 text-xs font-medium text-plat-muted hover:text-plat-fg"
      >
        <CaretLeft className="h-3.5 w-3.5" /> Support queue
      </button>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-plat-fg">{ticket.title}</h2>
          <p className="mt-0.5 text-sm text-plat-muted">
            Raised {fmt(ticket.createdAt)} · {ticket.category.replace(/_/g, ' ')}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* WHERE THE TICKET IS, then WHAT MAY HAPPEN TO IT NEXT. This replaces a status
              dropdown that offered all four states regardless of the current one — including
              `open → resolved`, which the API refuses. The badge keeps the status visible now
              that the select no longer doubles as the display. */}
          <SupportStatusBadge status={ticket.status} />
          <SupportLifecycleActions
            status={ticket.status}
            disabled={updateMutation.isPending}
            onAction={(to) => updateMutation.mutate({ status: to })}
          />
          <select
            value={ticket.priority}
            onChange={(e) => updateMutation.mutate({ priority: e.target.value })}
            className="h-8 rounded-lg border border-plat-border bg-plat-bg px-2 text-xs capitalize text-plat-secondary focus:outline-none"
          >
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {/* WHO OWNS IT. Its own row rather than crammed beside status/priority: "who is handling
          this" is the first question of a queue, and it was the one fact the page already
          received from the API and never showed. */}
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-xl border border-plat-border bg-plat-surface px-4 py-3 sm:px-5">
        <AssigneeControl ticketId={ticket.id} assignee={ticket.assignee} />
        <span className="text-xs text-plat-muted">
          {ticket.assignee
            ? 'This ticket has an owner.'
            : 'Nobody owns this ticket yet.'}
        </span>
      </div>

      {/* WHO — the first four questions of the brief, answered without a search. */}
      <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">Requester</h3>
        {ticket.requesterUserId ? (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className="flex items-center gap-2 font-medium text-plat-fg">
              <UserIcon className="h-4 w-4 text-plat-muted" />
              {ticket.requesterName ?? ticket.requesterEmail ?? 'Unknown'}
            </span>
            <span className="text-plat-secondary">{roleLabel(ticket.requesterRole)}</span>
            <span className="text-plat-secondary">{ticket.chambers?.name ?? 'No organisation'}</span>
            <span className="text-plat-muted">{ticket.requesterEmail}</span>
            <button
              onClick={() => navigate(`/platform/users/${ticket.requesterUserId}`)}
              className="text-xs font-medium text-plat-info-text hover:underline"
            >
              View account
            </button>
          </div>
        ) : (
          <p className="text-sm text-plat-muted">
            Opened from the platform console — no customer requester.
          </p>
        )}
      </div>

      {/* WHAT they said. */}
      <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">Customer report</h3>
        <p className="whitespace-pre-wrap text-sm text-plat-fg">{ticket.body}</p>
        {ticket.reportedFailure && (
          <div className="mt-4 rounded-lg border border-plat-border bg-plat-bg p-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-plat-fg">
              <Bug className="h-3.5 w-3.5" /> Reported by the app as
              <span className="font-mono">{ticket.reportedFailure.code}</span>
            </div>
            <p className="mt-1.5 text-xs text-plat-secondary">{ticket.reportedFailure.explanation}</p>
            {ticket.reportedCorrelationId && (
              <button
                onClick={() => copy(ticket.reportedCorrelationId!, 'Request ID')}
                className="mt-2 inline-flex items-center gap-1 rounded border border-plat-border px-1.5 py-0.5 font-mono text-[11px] text-plat-secondary hover:bg-plat-hover"
              >
                <Copy className="h-3 w-3" /> {ticket.reportedCorrelationId}
              </button>
            )}
          </div>
        )}
      </div>

      {/* INVESTIGATION — contextual, opened deliberately (and audited when opened). */}
      <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">Investigation</h3>
          <div className="flex gap-2">
            {!investigating && (
              <button
                onClick={() => setInvestigating(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-plat-border px-3 py-1.5 text-xs font-medium text-plat-secondary hover:bg-plat-hover"
              >
                <MagnifyingGlass className="h-3.5 w-3.5" /> Investigate issue
              </button>
            )}
            <button
              onClick={() => setEscalateOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-plat-border px-3 py-1.5 text-xs font-medium text-plat-secondary hover:bg-plat-hover"
            >
              <Warning className="h-3.5 w-3.5" /> Escalate to developer
            </button>
          </div>
        </div>
        <div className="mt-4">
          {investigating
            ? <InvestigationPanel ticketId={ticket.id} reportedCorrelationId={ticket.reportedCorrelationId} />
            : <p className="text-sm text-plat-muted">
                Correlates this requester&apos;s recent activity around the time the ticket was
                raised and highlights any failure. Opening it is recorded in the Activity Log.
              </p>}
        </div>
      </div>

      {/* CONVERSATION */}
      <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">Conversation</h3>
        {/* The SAME component the queue's row-level 💬 action opens in a drawer — one thread,
            one composer, one query key. See SupportConversation.tsx. */}
        <SupportConversation ticketId={ticket.id} />
      </div>

      {escalateOpen && <EscalationModal ticketId={ticket.id} onClose={() => setEscalateOpen(false)} />}
    </div>
  );
}
